import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { useLocation, useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface BlueskyAuthProps {
  setSession: (session: {
    token: string;
    did: string;
    username: string;
    expiresAt: number;
  }) => Promise<void>;
}

export function BlueskyAuth({ setSession }: BlueskyAuthProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [handle, setHandle] = useState<string>('');
  const { toast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();

  // Handle the OAuth callback
  useEffect(() => {
    const handleCallback = async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const state = params.get('state');
      
      if (code && state) {
        try {
          setLoading(true);
          
          // Call the Worker function to complete OAuth
          const response = await fetch(`/oauth/callback?${params.toString()}`);
          if (!response.ok) {
            throw new Error('Failed to complete authorization');
          }
          
          const { token, did, username } = await response.json();
          
          // Store the session
          await setSession({
            token,
            did,
            username,
            expiresAt: Date.now() + 48 * 60 * 60 * 1000, // 48 hours
          });
          
          // Redirect to the chat
          navigate('/chat');
        } catch (err) {
          console.error('Error completing OAuth flow:', err);
          toast({
            title: 'Error',
            description: 'Failed to complete login process',
            variant: 'destructive',
          });
        } finally {
          setLoading(false);
        }
      }
    };
    
    handleCallback();
  }, [navigate, setSession, toast]);

  // Validate the handle format
  const validateHandle = (input: string): boolean => {
    // Basic validation for Bluesky handles
    // Should be non-empty and contain only valid characters
    // Allow dots for domain names
    return /^[a-zA-Z0-9._-]+$/.test(input);
  };

  // Start the OAuth flow
  const handleLogin = async () => {
    if (!handle.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter a Bluesky handle',
        variant: 'destructive',
      });
      return;
    }
    
    // Clean up the handle
    let userHandle = handle.trim();
    
    // Remove @ prefix if present
    if (userHandle.startsWith('@')) {
      userHandle = userHandle.substring(1);
    }
    
    // Validate handle format
    if (!validateHandle(userHandle)) {
      toast({
        title: 'Invalid Handle',
        description: 'Please enter a valid Bluesky handle',
        variant: 'destructive',
      });
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      console.log('Initiating OAuth flow for handle:', userHandle);
      
      // Call the Worker function to initiate OAuth
      const response = await fetch(`/oauth/authorize?handle=${encodeURIComponent(userHandle)}`);
      const responseData = await response.json().catch(() => null);
      
      if (!response.ok) {
        console.error('OAuth error response:', responseData);
        throw new Error(responseData?.details || 'Failed to initiate authorization');
      }

      // Get the redirect URL from the response
      const redirectUrl = response.url;
      if (!redirectUrl) {
        throw new Error('No redirect URL received');
      }

      console.log('Received redirect URL:', redirectUrl);
      
      // Perform the redirect
      window.location.href = redirectUrl;
    } catch (err) {
      console.error('Error initiating OAuth flow:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to initiate login process';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardTitle>Sign in with Bluesky</CardTitle>
        <CardDescription>
          Enter your Bluesky handle to continue
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="handle">Bluesky Handle</Label>
            <Input
              id="handle"
              placeholder="bsky.social"
              value={handle}
              onChange={(e) => setHandle(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
            />
          </div>
          {error && (
            <div className="text-sm text-red-500">
              {error}
            </div>
          )}
        </div>
      </CardContent>
      <CardFooter>
        <Button
          className="w-full"
          onClick={handleLogin}
          disabled={loading}
        >
          {loading ? 'Signing in...' : 'Sign in with Bluesky'}
        </Button>
      </CardFooter>
    </Card>
  );
}

export default BlueskyAuth;
